import { computed, inject, type Signal } from '@angular/core';
import { type DateRange } from '@angular/material/datepicker';
import { type AvailableIncidentFiltersDataDto, IncidentsV2Service } from '@api/incidents';
import { defaultSelectOption, NavigationService, type SelectExtendedOption, type SelectOption } from '@core';
import { incidentColumnOptions, IncidentTabStatusFilter } from '@models';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AvsDateRangeIds, getAvsDateRange } from '@ui';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';

export interface IncidentsFilterCriteria {
  deviceId?: string;
  deviceStatus?: string[];
  deviceTypeId?: string[];
  endDateTime?: string;
  locationId?: string;
  query?: string; // ticket-number substring search, now server-side
  severity?: string[];
  spaceId?: string;
  startDateTime?: string;
}

export type IncidentsScope = Pick<IncidentsFilterCriteria, 'deviceId' | 'locationId' | 'spaceId'>;

export interface IncidentsSortCriteria {
  field: string;
  order: 'asc' | 'desc';
}

export interface FilterOption extends SelectOption {
  id?: string;
}

export interface FilterExtendedOption extends SelectExtendedOption {
  id?: string;
}

interface IncidentsCriteriaState {
  availableFilters: AvailableIncidentFiltersDataDto | null;
  buildingOptions: FilterOption[];
  columnOptions: SelectOption[];
  deviceTypeOptions: FilterExtendedOption[];
  displayedColumns: string[];
  excludedColumns: string[];
  floorOptions: FilterOption[];
  resolvedDateRangeId: AvsDateRangeIds;
  resolvedDates: { endDateTime: string; startDateTime: string; };
  resolvedIncidentsBuildingOptions: FilterOption[];
  resolvedIncidentsDeviceTypeOptions: FilterExtendedOption[];
  resolvedIncidentsFloorOptions: FilterOption[];
  resolvedIncidentsRoomOptions: FilterOption[];
  resolvedIncidentsSelectedBuilding: string;
  resolvedIncidentsSelectedFloor: string;
  resolvedIncidentsSelectedRoom: string;
  roomOptions: FilterOption[];
  search: string;
  selectedBuilding: string;
  selectedFilters: IncidentsFilterCriteria;
  selectedFloor: string;
  selectedRoom: string;
  severityOptions: FilterExtendedOption[];
  sortCriteria: IncidentsSortCriteria | null;
  tab: IncidentTabStatusFilter;
}

/*
 * Fresh object + fresh arrays every call, not a shared module-level constant —
 * `reset()` calls this too, and `patchState` assigns whatever's passed by
 * reference. A shared constant would mean every reset (and every store
 * instance) pointed its "empty" array fields at the exact same `[]`
 * instances; harmless today since no method mutates an array in place
 * (all use `.map`/spread), but a single future `.push()` would corrupt the
 * template for every store past and future. Cheap to avoid outright instead
 * of relying on that discipline holding forever.
 */
function createInitialState() {
  /*
   * Default tab is Open, so the initial column list excludes 'resolved' the
   * same way `setTab` would if switching *to* Open — and, since `displayedColumns`
   * drives `matHeaderRowDef`/`matRowDef` directly (the table's static index/
   * ticketNumber columns still need to be listed there to render at all), it
   * must go through `generateTableColumns` for that prefix too, not just the
   * raw `incidentColumnOptions` value list.
   */
  const initialOpenColumns = incidentColumnOptions.filter(({ value }) => value !== 'resolved').map(option => option.value);

  return {
    availableFilters: null,
    buildingOptions: [],
    columnOptions: buildColumnOptions(true, []),
    deviceTypeOptions: [],
    displayedColumns: generateTableColumns(initialOpenColumns),
    excludedColumns: [],
    floorOptions: [],
    resolvedDateRangeId: AvsDateRangeIds.TODAY,
    resolvedDates: { endDateTime: '', startDateTime: '' },
    resolvedIncidentsBuildingOptions: [],
    resolvedIncidentsDeviceTypeOptions: [],
    resolvedIncidentsFloorOptions: [],
    resolvedIncidentsRoomOptions: [],
    resolvedIncidentsSelectedBuilding: '',
    resolvedIncidentsSelectedFloor: '',
    resolvedIncidentsSelectedRoom: '',
    roomOptions: [],
    search: '',
    selectedBuilding: '',
    selectedFilters: {},
    selectedFloor: '',
    selectedRoom: '',
    severityOptions: [],
    sortCriteria: null,
    tab: IncidentTabStatusFilter.OPEN,
  };
}

function generateFloorOptions(availableFilters: AvailableIncidentFiltersDataDto, selectedBuilding: string) {
  const buildingId = availableFilters.buildings.find(({ title }) => title === selectedBuilding)?.value;

  return [
    defaultSelectOption,
    ...availableFilters.floors
      .filter(floor => floor.parentId === buildingId)
      .map(floor => ({ id: floor.value, title: floor.title, value: floor.title })),
  ];
}

function generateRoomOptions(availableFilters: AvailableIncidentFiltersDataDto, selectedFloor: string) {
  const floorId = availableFilters.floors.find(({ title }) => title === selectedFloor)?.value;

  return [
    defaultSelectOption,
    ...availableFilters.rooms
      .filter(room => room.parentId === floorId)
      .map(room => ({ id: room.value, title: room.title, value: room.title })),
  ];
}

function findOptionId(options: FilterOption[], value: string) {
  return options.find(option => option.value === value)?.id;
}

function keepChecked(options: FilterExtendedOption[], previous: FilterExtendedOption[], key: 'id' | 'value') {
  const checked = new Set(previous.filter(option => option.checked).map(option => option[key]));

  return options.map(option => ({
    ...option,
    checked: checked.has(option[key]),
  }));
}

function toResolvedDates(range: DateRange<Date>) {
  if (!range.start || !range.end) {
    return null;
  }

  const endDate = new Date(range.end);

  endDate.setDate(endDate.getDate() + 1);

  return {
    endDateTime: endDate.toISOString(),
    startDateTime: new Date(range.start).toISOString(),
  };
}

function generateTableColumns(values: string[] = []) {
  return ['index', 'ticketNumber', ...values];
}

function buildColumnOptions(isOpen: boolean, excludedColumns: string[]) {
  return incidentColumnOptions.filter(({ value }) => !excludedColumns.includes(value) && !(isOpen && value === 'resolved'));
}

/**
 * Component-scoped: each host lists it in its own `providers`, so state never leaks between pages or lists.
 * - `selectedFilters`/`sortCriteria`: the wire-format shape sent to the backend. Hosts add their scope on top
 *   when binding the table; the scope is never stored here.
 * - everything else: UI selection state (dropdown options, selected location, column picker, date range).
 */
export const IncidentsCriteriaStore = signalStore(
  withState<IncidentsCriteriaState>(createInitialState()),
  withComputed(store => ({
    deviceTypeIds: computed(() => store.deviceTypeOptions().filter(option => option.checked).map(option => option.id!)),
    deviceTypeValue: computed(() => store.deviceTypeOptions().filter(option => option.checked).map(option => option.value)),
    filtersApplied: computed(() => Boolean(
      store.selectedRoom().length ||
      store.selectedFloor().length ||
      store.selectedBuilding().length ||
      store.deviceTypeOptions().some(option => option.checked) ||
      store.search().length,
    )),
    hasSelectedFilters: computed(() => Boolean(
      store.selectedBuilding() ||
      store.selectedFloor() ||
      store.selectedRoom() ||
      store.severityOptions().some(option => option.checked) ||
      store.deviceTypeOptions().some(option => option.checked),
    )),
    resolvedIncidentsDeviceTypeIds: computed(() =>
      store.resolvedIncidentsDeviceTypeOptions().filter(option => option.checked).map(option => option.id!),
    ),
    resolvedIncidentsDeviceTypeValue: computed(() =>
      store.resolvedIncidentsDeviceTypeOptions().filter(option => option.checked).map(option => option.value),
    ),
    resolvedIncidentsSelectedFilters: computed(() => Boolean(
      store.resolvedIncidentsSelectedBuilding() ||
      store.resolvedIncidentsSelectedFloor() ||
      store.resolvedIncidentsSelectedRoom() ||
      store.resolvedIncidentsDeviceTypeOptions().some(option => option.checked),
    )),
    selectedBuildingId: computed(() => store.buildingOptions().find(({ title }) => title === store.selectedBuilding())?.id),
    selectedFloorId: computed(() => store.floorOptions().find(({ value }) => value === store.selectedFloor())?.id),
    selectedRoomId: computed(() => store.roomOptions().find(({ value }) => value === store.selectedRoom())?.id),
    severityFilter: computed(() => store.severityOptions().filter(option => option.checked).map(option => option.value)),
  })),
  withMethods(store => ({
    clearOpenFilters() {
      patchState(store, {
        deviceTypeOptions: store.deviceTypeOptions().map(option => ({ ...option, checked: false })),
        selectedBuilding: '',
        selectedFloor: '',
        selectedRoom: '',
        severityOptions: store.severityOptions().map(option => ({ ...option, checked: false })),
      });
    },
    clearResolvedFilters() {
      patchState(store, {
        resolvedIncidentsDeviceTypeOptions: store.resolvedIncidentsDeviceTypeOptions().map(option => ({ ...option, checked: false })),
        resolvedIncidentsSelectedBuilding: '',
        resolvedIncidentsSelectedFloor: '',
        resolvedIncidentsSelectedRoom: '',
      });
    },
    /** Scoped lists: seeds the visible columns and removes scope-redundant ones (e.g. `building`) from the picker. */
    initColumns(columns: string[], excludedColumns: string[] = []) {
      const isOpen = store.tab() === IncidentTabStatusFilter.OPEN;
      const visibleColumns = columns.filter(column => !excludedColumns.includes(column) && !(isOpen && column === 'resolved'));

      patchState(store, {
        columnOptions: buildColumnOptions(isOpen, excludedColumns),
        displayedColumns: generateTableColumns(visibleColumns),
        excludedColumns,
      });
    },
    /* Mirrors the same available filters into the Open- and History-tab option lists; v2 has no row-based source for the latter. */
    loadAvailableFilters(availableFilters: AvailableIncidentFiltersDataDto) {
      const deviceTypeOptions = availableFilters.deviceTypes.map(option => ({
        checked: false,
        id: option.value,
        title: option.title,
        value: option.title,
      }));
      const severityOptions = availableFilters.severities.map(severity => ({
        checked: false,
        title: severity.charAt(0).toUpperCase() + severity.slice(1),
        value: severity,
      }));
      const buildingOptions = [
        defaultSelectOption,
        ...availableFilters.buildings.map(option => ({ id: option.value, title: option.title, value: option.title })),
      ];

      /* A reload (scope change on a reused host) keeps still-valid selections and drops the rest from the criteria too. */
      const selectedFilters = store.selectedFilters();
      const severities = new Set<string>(availableFilters.severities);
      const deviceTypeIds = new Set(availableFilters.deviceTypes.map(option => option.value));
      const deviceTypeId = selectedFilters.deviceTypeId?.filter(id => deviceTypeIds.has(id));
      const severity = selectedFilters.severity?.filter(value => severities.has(value));

      if (deviceTypeId?.length !== selectedFilters.deviceTypeId?.length || severity?.length !== selectedFilters.severity?.length) {
        patchState(store, s => ({
          selectedFilters: {
            ...s.selectedFilters,
            deviceTypeId,
            severity,
          },
        }));
      }

      patchState(store, {
        availableFilters,
        buildingOptions,
        deviceTypeOptions: keepChecked(deviceTypeOptions, store.deviceTypeOptions(), 'id'),
        floorOptions: generateFloorOptions(availableFilters, store.selectedBuilding()),
        resolvedIncidentsBuildingOptions: buildingOptions,
        resolvedIncidentsDeviceTypeOptions: keepChecked(deviceTypeOptions, store.resolvedIncidentsDeviceTypeOptions(), 'id'),
        resolvedIncidentsFloorOptions: generateFloorOptions(availableFilters, store.resolvedIncidentsSelectedBuilding()),
        resolvedIncidentsRoomOptions: generateRoomOptions(availableFilters, store.resolvedIncidentsSelectedFloor()),
        roomOptions: generateRoomOptions(availableFilters, store.selectedFloor()),
        severityOptions: keepChecked(severityOptions, store.severityOptions(), 'value'),
      });
    },
    reset() {
      patchState(store, createInitialState());
    },
    selectBuilding(value: string) {
      const availableFilters = store.availableFilters();

      patchState(store, {
        floorOptions: availableFilters ? generateFloorOptions(availableFilters, value) : [],
        roomOptions: [],
        selectedBuilding: value,
        selectedFloor: '',
        selectedRoom: '',
      });
    },
    selectFloor(value: string) {
      const availableFilters = store.availableFilters();

      patchState(store, {
        roomOptions: availableFilters ? generateRoomOptions(availableFilters, value) : [],
        selectedFloor: value,
        selectedRoom: '',
      });
    },
    selectResolvedBuilding(value: string) {
      const availableFilters = store.availableFilters();

      patchState(store, {
        resolvedIncidentsFloorOptions: availableFilters ? generateFloorOptions(availableFilters, value) : [],
        resolvedIncidentsRoomOptions: [],
        resolvedIncidentsSelectedBuilding: value,
        resolvedIncidentsSelectedFloor: '',
        resolvedIncidentsSelectedRoom: '',
      });
    },
    selectResolvedFloor(value: string) {
      const availableFilters = store.availableFilters();

      patchState(store, {
        resolvedIncidentsRoomOptions: availableFilters ? generateRoomOptions(availableFilters, value) : [],
        resolvedIncidentsSelectedFloor: value,
        resolvedIncidentsSelectedRoom: '',
      });
    },
    selectResolvedRoom(value: string) {
      patchState(store, { resolvedIncidentsSelectedRoom: value });
    },
    selectRoom(value: string) {
      patchState(store, { selectedRoom: value });
    },
    setDeviceTypeValue(values: string[]) {
      patchState(store, {
        deviceTypeOptions: store.deviceTypeOptions().map(option => ({ ...option, checked: values.includes(option.value) })),
      });
    },
    /* Drops the 'resolved' column on the Open tab and keeps it selectable on History. */
    setDisplayedColumns(values: string[]) {
      const isOpen = store.tab() === IncidentTabStatusFilter.OPEN;
      const filtered = isOpen ? values.filter(value => value !== 'resolved') : values;

      patchState(store, { displayedColumns: generateTableColumns(filtered) });
    },
    /* End date is pushed one day out to include the whole day; also patched onto `selectedFilters`. */
    setResolvedDateRange(range: DateRange<Date>) {
      const dates = toResolvedDates(range);
      const current = store.resolvedDates();

      if (!dates || (current.startDateTime === dates.startDateTime && current.endDateTime === dates.endDateTime)) {
        return;
      }

      patchState(store, s => ({
        resolvedDates: dates,
        selectedFilters: { ...s.selectedFilters, ...dates },
      }));
    },
    setResolvedDateRangeId(id: AvsDateRangeIds) {
      patchState(store, { resolvedDateRangeId: id });
    },
    setResolvedDeviceTypeValue(values: string[]) {
      patchState(store, {
        resolvedIncidentsDeviceTypeOptions: store
          .resolvedIncidentsDeviceTypeOptions()
          .map(option => ({ ...option, checked: values.includes(option.value) })),
      });
    },
    setSearch(search: string) {
      patchState(store, { search });
    },
    setSelectedFilters(filters: Partial<IncidentsFilterCriteria>) {
      patchState(store, s => ({ selectedFilters: { ...s.selectedFilters, ...filters } }));
    },
    setSeverityFilter(values: string[]) {
      patchState(store, {
        severityOptions: store.severityOptions().map(option => ({ ...option, checked: values.includes(option.value) })),
      });
    },
    setSortCriteria(sortCriteria: IncidentsSortCriteria | null) {
      patchState(store, { sortCriteria });
    },
    /* Toggles the 'resolved' column in or out of the options and the active columns on every tab switch. */
    setTab(tab: IncidentTabStatusFilter) {
      if (store.tab() === tab) {
        return;
      }

      const isOpen = tab === IncidentTabStatusFilter.OPEN;
      const columnOptions = buildColumnOptions(isOpen, store.excludedColumns());

      const currentColumns = store.displayedColumns();
      let displayedColumns = currentColumns;

      if (isOpen) {
        displayedColumns = currentColumns.filter(column => column !== 'resolved');
      } else if (!currentColumns.includes('resolved')) {
        const reportedIdx = currentColumns.indexOf('reported');

        displayedColumns = reportedIdx !== -1
          ? [...currentColumns.slice(0, reportedIdx + 1), 'resolved', ...currentColumns.slice(reportedIdx + 1)]
          : [...currentColumns, 'resolved'];
      }

      patchState(store, { columnOptions, displayedColumns, search: '', tab });
    },
    updateDeviceTypeValue(filter: { checked: boolean; value: string; }) {
      patchState(store, {
        deviceTypeOptions: store
          .deviceTypeOptions()
          .map(option => option.value === filter.value ? { ...option, checked: filter.checked } : option),
      });
    },
    updateResolvedDeviceTypeValue(filter: { checked: boolean; value: string; }) {
      patchState(store, {
        resolvedIncidentsDeviceTypeOptions: store.resolvedIncidentsDeviceTypeOptions().map(
          option => option.value === filter.value ? { ...option, checked: filter.checked } : option,
        ),
      });
    },
  })),
  withMethods(store => {
    const incidentsV2Service = inject(IncidentsV2Service);
    const navigationService = inject(NavigationService);

    return {
      /*
       * `null`/`undefined` skips the fetch; any object fetches for its ids (`{}` = client-wide).
       * A new scope cancels the in-flight request.
       */
      loadAvailableFiltersFor: rxMethod<IncidentsScope | null | undefined>(pipe(
        switchMap(scope => {
          if (!scope) {
            return EMPTY;
          }

          const { locationId, spaceId } = scope;

          return incidentsV2Service
            .getAvailableFilters({ clientId: navigationService.getCurrentClientId() }, {
              locationId,
              spaceId,
            })
            .pipe(
              tap(({ data }) => store.loadAvailableFilters(data)),
              /* Caught per request so one failure doesn't stop later scopes from loading; the HTTP interceptor reports it. */
              catchError(() => EMPTY),
            );
        }),
      )),

      /* Scope is spread last so it always wins over a filter patch (e.g. `locationId: undefined`). */
      scopedSelectedFilters(scope: Signal<IncidentsScope>) {
        return computed(() => ({
          ...store.selectedFilters(),
          ...scope(),
        }));
      },
    };
  }),
  withMethods(store => ({
    /** Tab switch for hosts without the desktop filters bar: rebuilds the target tab's criteria from its own selection. */
    switchTab(tab: IncidentTabStatusFilter) {
      store.setTab(tab);

      if (tab === IncidentTabStatusFilter.OPEN) {
        store.setSelectedFilters({
          deviceTypeId: store.deviceTypeIds(),
          endDateTime: undefined,
          locationId: store.selectedBuildingId(),
          severity: store.severityFilter(),
          spaceId: store.selectedRoomId() || store.selectedFloorId(),
          startDateTime: undefined,
        });

        return;
      }

      const storedDates = store.resolvedDates();
      const dates = storedDates.startDateTime && storedDates.endDateTime
        ? storedDates
        : toResolvedDates(getAvsDateRange(store.resolvedDateRangeId()));

      if (dates) {
        patchState(store, { resolvedDates: dates });
      }

      store.setSelectedFilters({
        deviceTypeId: store.resolvedIncidentsDeviceTypeIds(),
        endDateTime: dates?.endDateTime,
        locationId: findOptionId(store.resolvedIncidentsBuildingOptions(), store.resolvedIncidentsSelectedBuilding()),
        severity: undefined,
        spaceId:
          findOptionId(store.resolvedIncidentsRoomOptions(), store.resolvedIncidentsSelectedRoom()) ||
          findOptionId(store.resolvedIncidentsFloorOptions(), store.resolvedIncidentsSelectedFloor()),
        startDateTime: dates?.startDateTime,
      });
    },
  })),
);
